import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import { useUserContext } from "../../../shared/contexts/UserContext";
import { handleErr } from "../standards/common/util";
import axios from "axios";
import { deltamathAPI } from "../../utils";
import Button from "../../../student/components/generic/button";
import { useDocumentTitle } from "usehooks-ts";
import { uniqueId } from "lodash";
import { DMConfirmModal } from "../../../shared/DMModal/DMConfirmModal";

type SearchTagDataType =
  | "Global Associations"
  | "Wildcard Words"
  | "Hyphenated Words";

// Standalone function for uploading the file
const uploadCSV = async ({
  file,
  token,
  url,
  type,
}: {
  file: File;
  token: string;
  url: string;
  type?: SearchTagDataType;
}) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.request({
    method: "post",
    url,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
    data: formData,
    params: { type },
  });
  return response.data;
};

/**
 * Utility to allow manager users to bulk edit search tags using a CSV file.
 */
export const BulkEditSearchTags = () => {
  useDocumentTitle("Bulk Edit Search Tags");
  const toastContext = useDeltaToastContext();
  const userContext = useUserContext();
  const token = userContext.getJWT();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const onSuccess = () => {
    toastContext.addToast({
      status: "Success",
      message: "Upload Complete",
    });
  };

  const onError = (error: unknown) => {
    toastContext.addToast({
      status: "Error",
      message: handleErr(error),
    });
  };

  const { mutate: uploadSkillcodeSearchTagFile } = useMutation({
    mutationFn: ({ file }: { file: File }) =>
      uploadCSV({
        file,
        token,
        url: deltamathAPI() + "/manager_new/module/bulk_edit_search_tags",
      }),
    onSuccess,
    onError,
  });

  const { mutate: uploadSearchTagAssociationFile } = useMutation({
    mutationFn: ({ file }: { file: File }) =>
      uploadCSV({
        file,
        token,
        url: deltamathAPI() + "/manager_new/module/bulk_edit_tags_associations",
      }),
    onSuccess,
    onError,
  });

  const { mutate: uploadMiscFile } = useMutation({
    mutationFn: ({ file, type }: { file: File; type?: SearchTagDataType }) =>
      uploadCSV({
        file,
        token,
        url: deltamathAPI() + "/manager_new/module/bulk_edit_misc_search",
        type,
      }),
    onSuccess,
    onError,
  });

  const { mutate: undoBulkEdit } = useMutation({
    mutationFn: async () => {
      await axios.request({
        method: "patch",
        url: deltamathAPI() + "/manager_new/module/undo_bulk_edit_search_tags",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess,
    onError,
  });

  const uploadOptions: {
    label: React.ReactNode;
    uploadFunction: ({
      file,
      type,
    }: {
      file: File;
      type?: SearchTagDataType;
    }) => void;
    btnText: string;
    title: string;
    type?: SearchTagDataType;
    undoButtonText?: string;
    undoFunction?: () => void;
  }[] = [
    {
      label: (
        <>
          Upload a CSV file to{" "}
          <strong>bulk edit search tags and associations</strong> for all
          supplied skill codes. The CSV file should contain the following
          columns:
          <ul className="list-disc pl-6">
            <li>Skill Code</li>
            <li>Search Tags (comma separated list)</li>
            <li>Associations (comma separated list)</li>
          </ul>
        </>
      ),
      uploadFunction: uploadSkillcodeSearchTagFile,
      btnText: "Upload Skill Code Search Tags",
      title: "Bulk Edit Skill Code Search Tags",
      undoButtonText: "Restore Latest Skill Code Version",
      undoFunction: () => {
        setShowConfirmModal(true);
      },
    },
    {
      label: (
        <>
          Upload a CSV file to{" "}
          <strong>bulk edit all search tags and associations</strong>. The CSV
          file should contain the following columns:
          <ul className="list-disc pl-6">
            <li>Search Tag</li>
            <li>Associations (comma separated list)</li>
          </ul>
        </>
      ),
      uploadFunction: uploadSearchTagAssociationFile,
      btnText: "Upload Search Tags and Associations",
      title: "Bulk Edit Search Tags and Associations",
    },
    {
      label: (
        <>
          Upload a CSV file to{" "}
          <strong>bulk edit all global associations</strong>. The CSV file
          should contain the following column:
          <ul className="list-disc pl-6">
            <li>All Corresponding Associations (comma separated list)</li>
          </ul>
        </>
      ),
      uploadFunction: uploadMiscFile,
      btnText: "Upload Global Associations",
      title: "Bulk Edit Global Associations",
      type: "Global Associations",
    },
    {
      label: (
        <>
          Upload a CSV file to <strong>bulk edit all wildcard words</strong>.
          The CSV file should contain the following columns:
          <ul className="list-disc pl-6">
            <li>Root Wildcard Word</li>
            <li>Corresponding Compound Word</li>
          </ul>
        </>
      ),
      uploadFunction: uploadMiscFile,
      btnText: "Upload Wildcard Words",
      title: "Bulk Edit Wildcard Words",
      type: "Wildcard Words",
    },
    {
      label: (
        <>
          Upload a CSV file to{" "}
          <strong>
            bulk edit all hyphenated words / one to two word phrases
          </strong>
          . The CSV file should contain the following columns:
          <ul className="list-disc pl-6">
            <li>Possible User Input (comma separated list)</li>
            <li>Correct Input for DeltaMath</li>
          </ul>
        </>
      ),
      uploadFunction: uploadMiscFile,
      btnText: "Upload Hyphenated Words",
      title: "Bulk Edit Hyphenated Words",
      type: "Hyphenated Words",
    },
  ];

  return (
    <>
      <div className="px-6 py-10">
        <h2 className="mb-6 text-2xl font-bold">Bulk Edit Search Tag Data</h2>
        <div className="flex flex-col gap-6">
          {uploadOptions.map(
            ({
              label,
              uploadFunction,
              btnText,
              title,
              type,
              undoButtonText,
              undoFunction,
            }) => (
              <FileInput
                key={btnText}
                uploadFunction={uploadFunction}
                label={label}
                btnText={btnText}
                title={title}
                type={type}
                undoButtonText={undoButtonText}
                undoFunction={undoFunction}
              />
            )
          )}
        </div>
        <DMConfirmModal
          title="Restore Latest Version"
          visible={showConfirmModal}
          onConfirm={() => {
            undoBulkEdit();
            setShowConfirmModal(false);
          }}
          onCancel={() => setShowConfirmModal(false)}
        >
          <p>
            Are you sure you want to restore the latest version? This will undo
            the last bulk edit and any actions since that edit.
          </p>
        </DMConfirmModal>
      </div>
    </>
  );
};

const FileInput = ({
  uploadFunction,
  label,
  btnText,
  title,
  type,
  undoButtonText,
  undoFunction,
}: {
  uploadFunction: ({
    file,
    type,
  }: {
    file: File;
    type?: SearchTagDataType;
  }) => void;
  label: React.ReactNode;
  btnText: string;
  title: string;
  type?: SearchTagDataType;
  undoButtonText?: string;
  undoFunction?: () => void;
}) => {
  const [selectedCSV, setSelectedCSV] = useState<File | null>(null);
  const inputID = uniqueId("file-input");
  return (
    <div>
      <h3 className="mb-2 text-lg font-bold">{title}</h3>
      <label htmlFor={inputID} className="mb-2 inline-block">
        {label}
      </label>
      <input
        className="focus:border-primary focus:shadow-te-primary dark:focus:border-primary relative mb-3 block w-2/5 min-w-0 flex-auto rounded border border-solid border-neutral-500 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:text-neutral-700 focus:outline-none dark:border-neutral-600 dark:text-neutral-200 dark:file:bg-neutral-700 dark:file:text-neutral-100"
        type="file"
        id={inputID}
        onChange={(event) => {
          if (event.target.files?.[0]) {
            setSelectedCSV(event.target.files[0]);
          } else {
            setSelectedCSV(null); // Reset if no file is selected
          }
        }}
      />
      <Button
        className="w-fit"
        onClick={() => {
          if (selectedCSV) {
            uploadFunction({ file: selectedCSV, type });
            setSelectedCSV(null); // Reset the input after upload
          }
        }}
        disabled={!selectedCSV}
      >
        {btnText}
      </Button>
      {undoButtonText && (
        <div className="mt-2">
          <Button
            className="w-fit bg-dm-error-500 text-white hover:bg-dm-error-600"
            onClick={undoFunction}
          >
            {undoButtonText}
          </Button>
        </div>
      )}
      <hr className="mt-4 border-neutral-500" />
    </div>
  );
};
